<template>
  <Container
    tagName="section"
    class="CollapsibleFeatureListing"
    :style="{ backgroundColor }"
  >
    <CenteredTextBox v-if="hasBodyText" :content="contentSansCta" />
    <div
      class="featureListing"
      :class="{
        'alignMediaRight': isMediaAlignedRight,
        'alignMediaLeft': !isMediaAlignedRight,
        'mediaWide': isMediaWide
      }"
    >
      <div class="mediaContainer">
        <AnyMedia class="media" :media="content.media" />
        <VideoThumb v-if="content.videoThumb" :content="content.videoThumb" />
      </div>
      <div class="features">
        <HeadingChunk v-if="!hasBodyText"
          :heading="content.heading"
          :label="content.label"
          class="headingChunk"
        />
        <FeatureDetails
          v-for="(feature, index) in content.features"
          :key="feature._key"
          :open="index === openedIndex"
          :content="feature"
          class="featureItem"
          :selectThisFeature="() => selectFeature(index)"
        />
      </div>
    </div>
    <div v-if="content.cta && content.cta.text" class="footer">
      <CtaButton
        class="--bright-blue"
        v-bind="content.cta"
      />
    </div>
  </Container>
</template>

<script>
import AnyMedia from '../../elems/AnyMedia.vue'
import CenteredTextBox from '../blocks/CenteredTextBox.vue'
import FeatureDetails from './FeatureDetails.vue'
import HeadingChunk from '../blocks/HeadingChunk.vue'
import VideoThumb from './VideoThumb.vue'

export default {
  name: 'FeatureGrid',
  components: {
    AnyMedia,
    CenteredTextBox,
    FeatureDetails,
    HeadingChunk,
    VideoThumb,
  },
  props: {
    content: Object,
  },
  computed: {
    backgroundColor() {
      switch (this.content.presentationOptions?.backgroundColor) {
        case 'pale-gray':
          return 'var(--bg-color-pale-gray)'
        default:
          return 'var(--bg-color-white)'
      }
    },
    isMediaAlignedRight() {
      return this.content.presentationOptions?.imageAlign === 'right'
    },
    isMediaWide() {
      return this.content.presentationOptions?.width === 'wide'
    },
    contentSansCta() {
      const { cta, ...contentSansCta } = this.content
      return contentSansCta
    },
    hasBodyText() {
      return Boolean(this.content.body)
    },
  },
  data() {
    return {
      openedIndex: 0,
    }
  },
  methods: {
    selectFeature(index) {
      this.openedIndex = index
    },
  },
}
</script>

<style scoped>

.CollapsibleFeatureListing {
  padding-top: var(--spacing-15);
  padding-bottom: var(--spacing-15);
  color: var(--text-color-dark);
}

.featureListing {
  margin-top: var(--spacing-12);
}

.mediaContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.media {
  width: 100%;  /* Without this, Safari fails size the media box correctly, at least for mux videos. */
}

.media:deep(img),
.media:deep(video) {
  border-radius: var(--border-radius);
}

.mediaContainer + .features {
  margin-top: var(--spacing-10);
}

.headingChunk + .featureItem {
  margin-top: var(--spacing-3);
}

.featureItem[disabled] {
  pointer-events: none;
  user-select: none;
}

.featureListing + .footer {
  margin-top: var(--spacing-13);
}

@media (--screen-lg) {

  .featureListing {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    column-gap: var(--spacing-4);
  }

  .featureListing .features,
  .featureListing .mediaContainer {
    grid-row: 1;
    align-self: center;
  }

  .featureListing.alignMediaLeft .features {
    grid-column: 7 / span 5;
  }

  .featureListing.alignMediaLeft.mediaWide .features {
    grid-column: 8 / span 5;
  }

  .featureListing.alignMediaLeft .mediaContainer {
    grid-column: 2 / span 4;
  }

  .featureListing.alignMediaLeft.mediaWide .mediaContainer {
    grid-column: 1 / span 6;
  }

  .featureListing.alignMediaRight .features {
    grid-column: 2 / span 5;
  }

  .featureListing.alignMediaRight.mediaWide .features {
    grid-column: 1 / span 5;
  }

  .featureListing.alignMediaRight .mediaContainer {
    grid-column: 8 / span 4;
  }

  .featureListing.alignMediaRight.mediaWide .mediaContainer {
    grid-column: 7 / span 6;
  }

  .featureListing .features {
    margin-top: 0;
  }

  .footer {
    text-align: center;
  }
}
</style>
